import { useRef, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { AccordionInlineData } from "./schemas";

const HEADER_OFFSET = 112;

export default function AccordionInline(props: AccordionInlineData) {
  const items = props.items ?? [];
  const [value, setValue] = useState<string>("");
  const triggerRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  const handleValueChange = (next: string) => {
    setValue(next);
    if (!next) return;
    [0, 120, 320, 520].forEach((delay) => {
      window.setTimeout(() => {
        requestAnimationFrame(() => {
          const el = triggerRefs.current.get(next);
          if (!el?.isConnected) return;
          const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
          window.scrollTo({ top, behavior: "auto" });
        });
      }, delay);
    });
  };

  return (
    <section className="relative w-full bg-background py-14 px-6 md:px-10">
      <div className="max-w-2xl mx-auto">
        {props.title && (
          <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">{props.title}</h2>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-foreground/55 italic">No items yet.</p>
        ) : (
          <Accordion
            type="single"
            collapsible
            value={value}
            onValueChange={handleValueChange}
            className="border-t border-foreground/15"
          >
            {items.map((it, i) => {
              const key = String(i);
              return (
                <AccordionItem key={i} value={key} className="border-foreground/15">
                  <AccordionTrigger
                    ref={(el) => {
                      if (el) triggerRefs.current.set(key, el);
                      else triggerRefs.current.delete(key);
                    }}
                    className="font-serif text-lg text-foreground py-5 hover:no-underline"
                  >
                    {it.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground/75 text-sm leading-relaxed pb-6 whitespace-pre-line">
                    {it.answer}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </section>
  );
}
